import React, { useState } from 'react';
import { FiXCircle, FiX, FiAlertCircle } from 'react-icons/fi';
import type { Claim } from './ClaimCard';

interface RejectClaimModalProps {
  claim: Claim | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (claimId: string, reason: string) => Promise<void>;
  loading: boolean;
}

const RejectClaimModal: React.FC<RejectClaimModalProps> = ({
  claim,
  isOpen,
  onClose,
  onConfirm,
  loading
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal closes
  const handleClose = () => {
    setReason('');
    setError(null);
    onClose();
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate reason is required
    if (!reason.trim()) {
      setError('กรุณาระบุเหตุผลในการปฏิเสธ');
      return;
    }

    if (!claim) return;

    try {
      await onConfirm(claim.id, reason.trim());
      handleClose();
    } catch (err: any) {
      setError(err.message || 'เกิดข้อผิดพลาดในการปฏิเสธ');
    }
  };

  if (!isOpen || !claim) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <FiXCircle className="h-6 w-6 text-red-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">
                ยืนยันการปฏิเสธคำขอ
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-500 transition-colors disabled:opacity-50"
            >
              <FiX className="h-6 w-6" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-4">
              {/* Company Info */}
              <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                <h3 className="text-sm font-medium text-red-900 mb-2">
                  ข้อมูลบริษัท
                </h3>
                <p className="text-base font-semibold text-red-900">
                  {claim.company?.nameTh || 'ไม่ระบุชื่อบริษัท'}
                </p>
                {claim.company?.nameEn && (
                  <p className="text-sm text-red-700">{claim.company.nameEn}</p>
                )}
              </div>

              {/* User Info */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  ผู้ขอสิทธิ์
                </h3>
                <p className="text-sm font-semibold text-gray-900">
                  {claim.user?.firstName && claim.user?.lastName
                    ? `${claim.user.firstName} ${claim.user.lastName}`
                    : 'ไม่ระบุชื่อ'}
                </p>
                <p className="text-sm text-gray-600">{claim.user?.email || 'ไม่มีอีเมล'}</p>
                {claim.user?.phone && (
                  <p className="text-sm text-gray-600">เบอร์โทร: {claim.user.phone}</p>
                )}
              </div>

              {/* User Notes */}
              {claim.notes && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    หมายเหตุจากผู้ขอ
                  </h3>
                  <p className="text-sm text-gray-900">{claim.notes}</p>
                </div>
              )}

              {/* Rejection Reason (Required) */}
              <div>
                <label htmlFor="reject-reason" className="block text-sm font-medium text-gray-700 mb-2">
                  เหตุผลในการปฏิเสธ <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="reject-reason"
                  rows={4}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="กรุณาระบุเหตุผลที่ชัดเจน เพื่อให้ผู้ขอสิทธิ์เข้าใจและสามารถแก้ไขได้..."
                  className={`w-full px-3 py-2 border ${
                    error && !reason.trim() ? 'border-red-300' : 'border-gray-300'
                  } rounded-md shadow-sm focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none`}
                  disabled={loading}
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  เหตุผลนี้จะถูกส่งไปยังผู้ขอสิทธิ์ผ่านการแจ้งเตือน
                </p>
                {error && !reason.trim() && (
                  <p className="mt-1 text-xs text-red-600">
                    กรุณาระบุเหตุผลในการปฏิเสธ
                  </p>
                )}
              </div>

              {/* Suggested Reasons */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  ตัวอย่างเหตุผลที่ใช้บ่อย
                </h4>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>ไม่สามารถยืนยันความเป็นเจ้าของได้ กรุณาส่งเอกสารเพิ่มเติม</li>
                  <li>บริษัทนี้มีเจ้าของแล้ว กรุณาติดต่อเจ้าของปัจจุบัน</li>
                  <li>ข้อมูลที่ให้มาไม่ตรงกับฐานข้อมูล</li>
                  <li>เอกสารที่แนบมาไม่ชัดเจนหรือไม่ถูกต้อง</li>
                </ul>
              </div>

              {/* Warning Message */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <FiAlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-red-900 mb-1">
                      ผลที่จะเกิดขึ้นหลังปฏิเสธ
                    </h4>
                    <ul className="text-sm text-red-800 space-y-1 list-disc list-inside">
                      <li>คำขอนี้จะถูกปฏิเสธและปิดการทำงาน</li>
                      <li>ผู้ใช้จะไม่กลายเป็นเจ้าของบริษัทนี้</li>
                      <li>ผู้ใช้จะได้รับการแจ้งเตือนพร้อมเหตุผล</li>
                      <li>ผู้ใช้สามารถส่งคำขอใหม่ได้ในภายหลัง</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && reason.trim() && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <FiAlertCircle className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-red-800">เกิดข้อผิดพลาด</h4>
                      <p className="text-sm text-red-700 mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                disabled={loading || !reason.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    กำลังดำเนินการ...
                  </>
                ) : (
                  <>
                    <FiXCircle className="mr-2 h-4 w-4" />
                    ยืนยันการปฏิเสธ
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default RejectClaimModal;
